/**
 * Pure view math for the hand-rolled Form Flow (Workflow) canvas.
 *
 * The canvas inner div is transformed with `translate(pan) scale(zoom)` and
 * `transform-origin: 0 0` (see WorkflowCanvas.jsx). Because the scale is about
 * the origin, the top-left corner of the inner element does not move under
 * zoom, so a point in inner-local coordinates (u, v) maps to screen space as:
 *
 *     clientX = rect.left + zoom * u
 *     clientY = rect.top  + zoom * v
 *
 * where `rect` is the inner element's *current* bounding rect (already includes
 * the current pan). Extracted here (rather than inline in the page) so the
 * cursor-anchoring and clamping behaviour can be unit-tested directly.
 */

// Zoom bounds shared by the wheel handler and the +/- buttons, so they can
// never drift apart. The buttons clamp on one axis each today; these clamp both
// axes, which is a strict superset and behaviour-compatible.
export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 2;
export const ZOOM_STEP = 0.1;

/**
 * Clamp a zoom value to [ZOOM_MIN, ZOOM_MAX].
 * @param {number} zoom
 * @returns {number}
 */
export const clampZoom = (zoom) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));

/**
 * Convert a `wheel` delta into a multiplicative zoom factor.
 *
 * A positive deltaY (scrolling down) zooms out (< 1), a negative deltaY zooms
 * in (> 1). The exponential form keeps the perceived rate even across the whole
 * range. Firefox reports line-mode deltas (deltaMode = 1, deltas ~3) which we
 * normalise to roughly pixel deltas (×16) so the two feel the same.
 *
 * @param {number} deltaY - raw `event.deltaY`
 * @param {number} [deltaMode] - `event.deltaMode` (0 = pixels, 1 = lines, 2 = pages)
 * @returns {number}
 */
export function wheelZoomFactor(deltaY, deltaMode = 0) {
    if (!Number.isFinite(deltaY)) return 1;
    const delta = deltaMode === 1 ? deltaY * 16 : deltaY;
    if (!Number.isFinite(delta)) return 1;
    return Math.exp(-delta * 0.002);
}

/**
 * Compute the view (zoom + pan) after zooming to `nextZoom`.
 *
 * When `rect` (the inner element's current bounding rect) and `clientX/clientY`
 * (the anchor point in screen space, e.g. the cursor) are given, the pan is
 * recomputed so the canvas point under the anchor stays under it — i.e.
 * cursor-anchored zoom rather than zoom toward the canvas origin. If `rect` is
 * missing (or the anchor is non-finite) it falls back to an un-anchored zoom
 * that keeps the current pan.
 *
 * @param {Object} args
 * @param {number} args.currentZoom - zoom before the change (must be finite and > 0)
 * @param {{x: number, y: number}} args.currentPan - pan before the change
 * @param {number} args.nextZoom - requested zoom (will be clamped)
 * @param {number} [args.clientX] - anchor x in screen space
 * @param {number} [args.clientY] - anchor y in screen space
 * @param {{left: number, top: number}} [args.rect] - inner element's bounding rect
 * @returns {{zoom: number, pan: {x: number, y: number}}}
 */
export function zoomAtPoint({ currentZoom, currentPan, nextZoom, clientX, clientY, rect }) {
    const zoom = clampZoom(nextZoom);
    if (!Number.isFinite(zoom)) {
        // Guard: NaN/Infinity input must not corrupt the view.
        return { zoom: currentZoom, pan: currentPan };
    }
    if (!rect || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        return { zoom, pan: currentPan };
    }
    if (!Number.isFinite(currentZoom) || currentZoom <= 0) {
        // Defensive: an invalid current zoom cannot be anchored — fall back to
        // an un-anchored zoom instead of dividing by zero and producing an
        // Infinity pan (which would corrupt the canvas transform).
        return { zoom, pan: currentPan };
    }
    const u = (clientX - rect.left) / currentZoom;
    const v = (clientY - rect.top) / currentZoom;
    return {
        zoom,
        pan: {
            x: currentPan.x + (currentZoom - zoom) * u,
            y: currentPan.y + (currentZoom - zoom) * v,
        },
    };
}
